import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Compass, BookOpen, Target, Coins, TrendingUp, Shield, Sparkles, ArrowUpRight } from "lucide-react";import { Skeleton } from "../components/ui/skeleton";

const PILLAR_META = {
  discover: { icon: Compass,    accent: "text-sky-400",     border: "hover:border-sky-400/60",     tagline: "Find a vocation that fits you" },
  learn:    { icon: BookOpen,   accent: "text-primary",     border: "hover:border-primary/60",     tagline: "271 volumes · fully searchable" },
  practice: { icon: Target,     accent: "text-emerald-400", border: "hover:border-emerald-400/60", tagline: "30 · 60 · 90 operating cadences" },
  earn:     { icon: Coins,      accent: "text-amber-400",   border: "hover:border-amber-400/60",   tagline: "Compensation grounded in evidence" },
  advance:  { icon: TrendingUp, accent: "text-fuchsia-400", border: "hover:border-fuchsia-400/60", tagline: "Career ladders & succession" },
  protect:  { icon: Shield,     accent: "text-rose-400",    border: "hover:border-rose-400/60",    tagline: "Rights, dignity, protection" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [pillars, setPillars] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/kb/stats").then(r => setStats(r.data)).catch(() => {}),
      api.get("/pillars").then(r => setPillars(r.data)).catch(() => setPillars([])),
    ]);
  }, []);

  return (
    <div className="space-y-12" data-testid="dashboard">
      <div>
        <div className="label-caps mb-2">Command of the tribunal</div>
        <h1 className="text-3xl sm:text-5xl tracking-tight font-semibold">
          Welcome, {user?.full_name?.split(" ")[0]}.
        </h1>
        <p className="text-muted-foreground mt-3 text-lg max-w-2xl">
          Six pillars. One disciplined operating system for your working life.
        </p>
      </div>

      {/* Tribunal spotlight */}
      <Card className="p-6 md:p-10 border-border grain relative overflow-hidden">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex-1">
            <Sparkles className="h-6 w-6 text-primary mb-4" />
            <h2 className="text-2xl md:text-3xl tracking-tight font-medium">Ask the Tribunal Advisor</h2>
            <p className="text-muted-foreground mt-2 max-w-xl">
              Grounded answers across executive leadership, governance, decision-making, careers and worker rights —
              with inline citations.
            </p>
          </div>
          <Link to="/app/tribunal">
            <Button size="lg" className="rounded-full" data-testid="dash-tribunal">
              Open advisor <ArrowUpRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </Card>

      {/* KB stats */}
      {stats ? (
        <div className="grid grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
          {[
            { k: "Volumes", v: stats.parts },
            { k: "Chapters", v: stats.chapters?.toLocaleString() },
            { k: "Chunks indexed", v: stats.chunks?.toLocaleString() },
            { k: "Learn", v: stats.per_pillar?.learn?.toLocaleString() },
            { k: "Protect", v: stats.per_pillar?.protect?.toLocaleString() },
          ].map((s) => (
            <Card key={s.k} className="p-4" data-testid={`dash-stat-${s.k.toLowerCase()}`}>
              <div className="label-caps">{s.k}</div>
              <div className="font-mono text-2xl md:text-3xl tabular-nums mt-2">{s.v ?? 0}</div>
            </Card>
          ))}
        </div>
      ) : <Skeleton className="h-24 w-full" />}

      {/* Six pillars */}
      <div>
        <div className="label-caps mb-4">Pillars</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pillars.map((p) => {
            const m = PILLAR_META[p.slug] || {};
            const Icon = m.icon || Sparkles;
            return (
              <Link key={p.slug} to={`/app/${p.slug}`} data-testid={`dash-pillar-${p.slug}`}>
                <Card className={`p-6 h-full border-border transition-colors duration-200 ${m.border || ""}`}>
                  <div className="flex items-start justify-between">
                    <Icon className={`h-5 w-5 ${m.accent || "text-primary"}`} />
                    <div className="font-mono text-xs text-muted-foreground">
                      {p.chapter_count?.toLocaleString()} ch.
                    </div>
                  </div>
                  <div className="mt-5 text-xl font-medium tracking-tight">{p.name}</div>
                  <div className="text-sm text-muted-foreground mt-1">{m.tagline || p.subtitle}</div>
                  <div className="mt-5 space-y-1">
                    {(p.featured || []).slice(0, 3).map((f) => (
                      <div key={f.id} className="text-xs text-muted-foreground truncate">· {f.title}</div>
                    ))}
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Tools */}
      <div>
        <div className="label-caps mb-4">Your tools</div>
        <div className="grid md:grid-cols-3 gap-4">
          <Link to="/app/paths" data-testid="dash-tool-paths">
            <Card className="p-6 h-full hover:border-primary/50 transition-colors duration-200">
              <BookOpen className="h-5 w-5 text-primary mb-3" />
              <div className="text-lg font-medium tracking-tight">Study Paths</div>
              <div className="text-sm text-muted-foreground mt-1">Guided curricula per role — track progress chapter by chapter.</div>
            </Card>
          </Link>
          <Link to="/app/journal" data-testid="dash-tool-journal">
            <Card className="p-6 h-full hover:border-emerald-400/50 transition-colors duration-200">
              <Target className="h-5 w-5 text-emerald-400 mb-3" />
              <div className="text-lg font-medium tracking-tight">Decision Journal</div>
              <div className="text-sm text-muted-foreground mt-1">Log decisions. Review them against the tribunal's judgment framework.</div>
            </Card>
          </Link>
          <Link to="/app/digest" data-testid="dash-tool-digest">
            <Card className="p-6 h-full hover:border-primary/50 transition-colors duration-200">
              <Sparkles className="h-5 w-5 text-primary mb-3" />
              <div className="text-lg font-medium tracking-tight">Weekly Digest</div>
              <div className="text-sm text-muted-foreground mt-1">Monday mornings: one grounded insight + three chapters for your vocation.</div>
            </Card>
          </Link>
        </div>
      </div>
    </div>
  );
}
